import { useEffect, useState, type ReactNode } from 'react';
import { Button, Field, HStack, Input, RadioGroup, Stack, Text } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import { parseDashboard, type Dashboard, type Grid } from '@casa/model';
import { createDashboard, keepNewDashboard } from '../data/dashboard.ts';
import { freeDeviceName, isDashboardName } from '../data/device_store.ts';
import { listStore, readFromStore, type WritableStore } from '../data/stores.ts';
import { dashboardFileFrom, isSameGrid } from '../editing/dashboard_edits.ts';
import { requestEditMode } from '../editing/editor.tsx';
import { ChromeError, ChromeNote } from '../ui/chrome_page.tsx';
import { SettingsGroup, SettingsRow } from './settings_ui.tsx';

// A new dashboard (requirements D-1 to D-3 in docs/specs/dashboard-editor.md, DB-3 in
// docs/specs/settings.md): the grid first, in words a user understands, and a name; it is kept in
// the current store at once and opens in edit mode, with one empty screen or made from a dashboard of the
// store (DB-3a, issue #9). The grid is chosen once (D-5).

type Kind = 'fixed' | 'scrolling';

/** Whole numbers from 1 to 200, or undefined. */
function cells(text: string) : number | undefined {
    const value = Number(text);

    return Number.isInteger(value) && value >= 1 && value <= 200 ? value : undefined;
}

export interface NewDashboardFormProps {
    shell: Bridge;
    /** Where it is kept: the current store. */
    store: WritableStore;
    onCreated: (dashboard: Dashboard) => void;
}

export function NewDashboardForm({ shell, store, onCreated }: NewDashboardFormProps) : ReactNode {
    const [kind, setKind] = useState<Kind>('fixed');
    const [name, setName] = useState('');
    // 64 × 64 for a new fixed grid (issue #35, DS-1); a file without a grid stays 48 × 48.
    const [columns, setColumns] = useState('64');
    const [rows, setRows] = useState('64');
    const [phoneColumns, setPhoneColumns] = useState('4');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();
    /** The store's dashboards, to start from; the one chosen, with its grid once read. */
    const [existing, setExisting] = useState<string[]>([]);
    const [from, setFrom] = useState<{ name: string; grid?: Grid | undefined }>();

    const grid = kind === 'fixed' ? { columns: cells(columns), rows: cells(rows) } : { columns: cells(phoneColumns), rows: undefined };
    const isValidGrid = grid.columns !== undefined && (kind === 'scrolling' || grid.rows !== undefined);
    const isValid = isValidGrid && isDashboardName(name.trim());
    const newGrid: Grid | undefined = !isValidGrid || grid.columns === undefined ? undefined : grid.rows === undefined ? { columns: grid.columns } : { columns: grid.columns, rows: grid.rows };

    // A name free on this device to start with; the server says so when it is taken there.
    useEffect(() => {
        if (store === 'device') {
            void freeDeviceName(shell, 'My dashboard').then(setName);
        } else {
            setName('My dashboard');
        }
    }, [shell, store]);

    useEffect(() => {
        listStore(shell, store).then((entries) => setExisting(entries.map((entry) => entry.name)), () => setExisting([]));
    }, [shell, store]);

    const chooseFrom = (fromName: string | undefined) : void => {
        setFrom(fromName === undefined ? undefined : { name: fromName });
        if (fromName === undefined) {
            return;
        }

        // Its grid, to say whether it is copied as it is or laid out again.
        readFromStore(shell, store, fromName).then(
            (text) => {
                const parsed = parseDashboard(text);
                setFrom((current) => (current?.name === fromName && parsed.ok ? { name: fromName, grid: parsed.dashboard.grid } : current));
            },
            () => {},
        );
    };

    const create = async () : Promise<void> => {
        if (!isValid || !newGrid) {
            return;
        }

        setBusy(true);
        setError(undefined);
        try {
            const dashboard = from
                ? await keepNewDashboard(shell, dashboardFileFrom(await readFromStore(shell, store, from.name), newGrid), store, name.trim())
                : await createDashboard(shell, newGrid, store, name.trim());
            requestEditMode();
            onCreated(dashboard);
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : String(caught));
        } finally {
            setBusy(false);
        }
    };

    const numberInput = (label: string, value: string, setValue: (value: string) => void) : ReactNode => (
        <Field.Root width="auto">
            <Field.Label>{label}</Field.Label>
            <Input width="24" inputMode="numeric" value={value} onChange={(event) => setValue(event.target.value)} />
        </Field.Root>
    );

    return (
        <Stack gap="4">
            <Field.Root invalid={name.trim() !== '' && !isDashboardName(name.trim())}>
                <Field.Label>Name</Field.Label>
                <Input aria-label="Name" value={name} onChange={(event) => setName(event.target.value)} />
                <Field.ErrorText>Letters, digits, spaces, ".", "_" and "-", at most 64.</Field.ErrorText>
            </Field.Root>
            <Text color="fg.muted" fontSize="sm">
                How its screens are laid out. Its columns and rows can be made larger later, not smaller; a different layout is a new dashboard.
            </Text>
            <RadioGroup.Root value={kind} onValueChange={(details) => setKind(details.value === 'scrolling' ? 'scrolling' : 'fixed')}>
                <Stack gap="3">
                    <RadioGroup.Item value="fixed" alignItems="flex-start">
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText>
                            <Text fontWeight="semibold">Fills the screen, no scrolling</Text>
                            <Text color="fg.muted" fontSize="sm">
                                For tablets and computers: the screen is divided into cells, and tiles are placed on them.
                            </Text>
                        </RadioGroup.ItemText>
                    </RadioGroup.Item>
                    {kind === 'fixed' ? (
                        <HStack pl="7" gap="4">
                            {numberInput('Columns', columns, setColumns)}
                            {numberInput('Rows', rows, setRows)}
                        </HStack>
                    ) : null}
                    <RadioGroup.Item value="scrolling" alignItems="flex-start">
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText>
                            <Text fontWeight="semibold">Scrolls, for phones</Text>
                            <Text color="fg.muted" fontSize="sm">
                                A few columns of square cells; the screen scrolls down as far as there are tiles.
                            </Text>
                        </RadioGroup.ItemText>
                    </RadioGroup.Item>
                    {kind === 'scrolling' ? <HStack pl="7">{numberInput('Columns', phoneColumns, setPhoneColumns)}</HStack> : null}
                </Stack>
            </RadioGroup.Root>
            {!isValidGrid ? <ChromeNote status="warning" text="Columns and rows are whole numbers from 1 to 200." /> : null}
            {existing.length > 0 ? (
                <SettingsGroup title="Start from">
                    <SettingsRow icon="square" title="An empty screen" opens={false} isCurrent={from === undefined} onClick={() => chooseFrom(undefined)} />
                    {existing.map((each) => (
                        <SettingsRow key={each} icon="table-cells-large" title={each} opens={false} isCurrent={from?.name === each} onClick={() => chooseFrom(each)} />
                    ))}
                </SettingsGroup>
            ) : null}
            {from?.grid && newGrid ? (
                <Text color="fg.muted" fontSize="sm">
                    {isSameGrid(from.grid, newGrid)
                        ? `"${from.name}" has the same layout: it is copied as it is.`
                        : newGrid.rows === undefined
                            ? `"${from.name}" has another layout: its tiles are stacked, as wide as the screen, in the order they are on it (top to bottom, left to right). Groups are left out.`
                            : `"${from.name}" has another layout: its tiles keep their size, each at the first free place (top to bottom, left to right), staggered from the top left where there is none. Groups are left out.`}
                </Text>
            ) : null}
            <Button colorPalette="blue" alignSelf="flex-start" disabled={!isValid} loading={busy} onClick={() => void create()}>
                Make the dashboard
            </Button>
            {error ? <ChromeError text={error} /> : null}
        </Stack>
    );
}
